import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  ClipboardCheck,
  Loader2,
  ScanLine,
  Palette,
  Puzzle,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';

interface Piece {
  id: string;
  work_order_id: string;
  nfc_uid: string | null;
  piece_number: number;
  status: string;
  shelf_id: string | null;
  work_order?: {
    id: string;
    order_number: string;
    product_name: string;
    customer: string;
    workflow_type: string;
    quantity: number;
    product_model_id?: string;
  };
  product_model?: {
    requires_paint: boolean;
    requires_inserts: boolean;
    is_composite: boolean;
  };
}

interface Shelf {
  id: string;
  code: string;
  name: string;
  min_skill_level: 'A' | 'B' | 'C';
  shelf_type: string;
}

const SKILL_LEVEL_COLORS: Record<string, string> = {
  A: 'bg-red-100 text-red-800',
  B: 'bg-yellow-100 text-yellow-800',
  C: 'bg-green-100 text-green-800',
};

export default function InspectorStation() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [nfcInput, setNfcInput] = useState('');

  // Diálogo de asignación
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState<Piece | null>(null);
  const [assignForm, setAssignForm] = useState({
    skill_level: 'C' as 'A' | 'B' | 'C',
    shelf_id: '',
    requires_paint: false,
    requires_inserts: false,
    is_composite: false,
  });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar piezas desmoldeadas sin OT de corte asignada
      const { data: piecesData, error: piecesError } = await api
        .from('work_order_pieces')
        .select(`
          *,
          work_order:work_orders!work_order_pieces_work_order_id_fkey(
            id, order_number, product_name, customer, workflow_type, quantity, product_model_id
          )
        `)
        .eq('status', 'ready_for_cutting')
        .is('shelf_id', null)
        .order('created_at', { ascending: false });

      if (piecesError) throw piecesError;

      // Cargar estanterías activas
      const { data: shelvesData, error: shelvesError } = await api
        .from('workshop_shelves')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (shelvesError) throw shelvesError;

      setPieces(piecesData || []);
      setShelves(shelvesData || []);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Error cargando datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNfcScan = async () => {
    if (!nfcInput.trim()) return;

    try {
      // Buscar pieza por NFC
      const { data: piece, error } = await api
        .from('work_order_pieces')
        .select(`
          *,
          work_order:work_orders!work_order_pieces_work_order_id_fkey(
            id, order_number, product_name, customer, workflow_type, quantity, product_model_id
          )
        `)
        .eq('nfc_uid', nfcInput.trim())
        .single();

      if (error || !piece) {
        toast({
          title: 'No encontrada',
          description: `No se encontró pieza con NFC: ${nfcInput}`,
          variant: 'destructive',
        });
        setNfcInput('');
        return;
      }

      openAssignDialog(piece);
      setNfcInput('');
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const openAssignDialog = async (piece: Piece) => {
    setSelectedPiece(piece);

    // Cargar datos del modelo de producto si existe
    let productData = null;
    if (piece.work_order?.product_model_id) {
      const { data } = await api
        .from('product_models')
        .select('requires_paint, requires_inserts, is_composite')
        .eq('id', piece.work_order.product_model_id)
        .single();
      productData = data;
    }

    setAssignForm({
      skill_level: 'C',
      shelf_id: '',
      requires_paint: productData?.requires_paint || false,
      requires_inserts: productData?.requires_inserts || false,
      is_composite: productData?.is_composite || false,
    });
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedPiece || !assignForm.shelf_id) {
      toast({
        title: 'Error',
        description: 'Selecciona una estantería',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // 1. Actualizar la pieza con la estantería asignada
      const { error: pieceError } = await api
        .from('work_order_pieces')
        .update({
          shelf_id: assignForm.shelf_id,
          status: 'in_shelf',
        })
        .eq('id', selectedPiece.id);

      if (pieceError) throw pieceError;

      // 2. Actualizar la OT padre con los flags de proceso
      const { error: woError } = await api
        .from('work_orders')
        .update({
          requires_paint: assignForm.requires_paint,
          requires_inserts: assignForm.requires_inserts,
          is_composite: assignForm.is_composite,
        })
        .eq('id', selectedPiece.work_order_id);

      if (woError) throw woError;

      // 3. Crear OT de Corte automáticamente
      const shelf = shelves.find(s => s.id === assignForm.shelf_id);
      const woParent = selectedPiece.work_order;

      const { error: cutError } = await api
        .from('work_orders')
        .insert({
          order_number: `${woParent?.order_number}/CUT`,
          product_name: woParent?.product_name,
          customer: woParent?.customer,
          customer_id: (woParent as any)?.customer_id,
          project_id: (woParent as any)?.project_id,
          quantity: 1,
          priority: 'normal',
          status: 'pending',
          workflow_type: 'cutting',
          source_shelf_id: assignForm.shelf_id,
          parent_work_order_id: selectedPiece.work_order_id,
          requires_paint: assignForm.requires_paint,
          requires_inserts: assignForm.requires_inserts,
          is_composite: assignForm.is_composite,
        });

      if (cutError) throw cutError;

      toast({
        title: 'Pieza asignada',
        description: `Pieza enviada a ${shelf?.name}. OT de Corte creada.`,
      });

      setAssignDialogOpen(false);
      setSelectedPiece(null);
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Filtrar estanterías por nivel seleccionado
  const filteredShelves = shelves.filter(s => {
    if (assignForm.skill_level === 'A') return s.min_skill_level === 'A';
    if (assignForm.skill_level === 'B') return s.min_skill_level === 'A' || s.min_skill_level === 'B';
    return true; // C puede ir a cualquier estantería
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6" />
              Estación del Inspector
            </h1>
            <p className="text-muted-foreground">
              Clasifica las piezas post-desmoldeo y asigna a estantería
            </p>
          </div>
        </div>

        {/* Escáner NFC */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" />
              Escanear Pieza
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input
                placeholder="Escanea o ingresa el NFC de la pieza..."
                value={nfcInput}
                onChange={(e) => setNfcInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNfcScan()}
                className="flex-1"
              />
              <Button onClick={handleNfcScan}>
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Lista de piezas pendientes */}
        <Card>
          <CardHeader>
            <CardTitle>Piezas Pendientes de Clasificación</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : pieces.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay piezas pendientes de clasificación</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pieza</TableHead>
                    <TableHead>OT</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>NFC</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pieces.map((piece) => (
                    <TableRow key={piece.id}>
                      <TableCell>#{piece.piece_number}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {piece.work_order?.order_number}
                        </Badge>
                      </TableCell>
                      <TableCell>{piece.work_order?.product_name}</TableCell>
                      <TableCell>{piece.work_order?.customer}</TableCell>
                      <TableCell>
                        {piece.nfc_uid ? (
                          <code className="text-xs">{piece.nfc_uid}</code>
                        ) : (
                          <span className="text-muted-foreground">Sin NFC</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => openAssignDialog(piece)}
                        >
                          <ArrowRight className="h-4 w-4 mr-1" />
                          Asignar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Diálogo de asignación */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Asignar Pieza a Estantería</DialogTitle>
            <DialogDescription>
              Pieza #{selectedPiece?.piece_number} - {selectedPiece?.work_order?.product_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Nivel de complejidad */}
            <div className="space-y-2">
              <Label>Nivel de Complejidad</Label>
              <Select
                value={assignForm.skill_level}
                onValueChange={(v: 'A' | 'B' | 'C') =>
                  setAssignForm({ ...assignForm, skill_level: v, shelf_id: '' })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">
                    <span className="flex items-center gap-2">
                      <Badge className={SKILL_LEVEL_COLORS['A']}>A</Badge>
                      Complejo (experto)
                    </span>
                  </SelectItem>
                  <SelectItem value="B">
                    <span className="flex items-center gap-2">
                      <Badge className={SKILL_LEVEL_COLORS['B']}>B</Badge>
                      Intermedio
                    </span>
                  </SelectItem>
                  <SelectItem value="C">
                    <span className="flex items-center gap-2">
                      <Badge className={SKILL_LEVEL_COLORS['C']}>C</Badge>
                      Básico
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Estantería destino */}
            <div className="space-y-2">
              <Label>Estantería Destino</Label>
              <Select
                value={assignForm.shelf_id}
                onValueChange={(v) => setAssignForm({ ...assignForm, shelf_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona estantería..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredShelves.map((shelf) => (
                    <SelectItem key={shelf.id} value={shelf.id}>
                      <span className="flex items-center gap-2">
                        <Badge className={SKILL_LEVEL_COLORS[shelf.min_skill_level]}>
                          {shelf.min_skill_level}
                        </Badge>
                        {shelf.code} - {shelf.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Opciones de proceso */}
            <div className="space-y-3 pt-2 border-t">
              <Label>Procesos Requeridos</Label>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-blue-500" />
                  <span>Requiere Pintura</span>
                </div>
                <Switch
                  checked={assignForm.requires_paint}
                  onCheckedChange={(v) =>
                    setAssignForm({ ...assignForm, requires_paint: v })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Puzzle className="h-4 w-4 text-orange-500" />
                  <span>Requiere Insertos</span>
                </div>
                <Switch
                  checked={assignForm.requires_inserts}
                  onCheckedChange={(v) =>
                    setAssignForm({ ...assignForm, requires_inserts: v })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Puzzle className="h-4 w-4 text-purple-500" />
                  <span>Es Pieza Compuesta</span>
                </div>
                <Switch
                  checked={assignForm.is_composite}
                  onCheckedChange={(v) =>
                    setAssignForm({ ...assignForm, is_composite: v })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAssign} disabled={saving || !assignForm.shelf_id}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Asignar y Crear OT Corte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
